import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { Masked, MaskedField, MaskedTeam } from "work/masked-fields";
import { publishedFeeScheduleTeamId } from "work/proposals/_views/drafts/tabs/apply-published-fee-schedule";
import PublishedFeeScheduleAction from "work/proposals/_views/drafts/tabs/published-fee-schedule-action";
import usePublishedFeeSchedules from "work/proposals/_views/drafts/tabs/use-published-fee-schedules";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** What the offer says. One line: it asks, and the button beside it acts. */
export const PUBLISHED_RATES_OFFER =
  "Your team publishes its rates. You can start from them, or set the fee schedule yourself.";

const Offer = styled(Alert)(({ theme }) => ({
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "& .MuiAlert-action": {
    alignItems: "center",
    gap: theme.spacing(1),
    paddingTop: 0,
  },
  // The action's own label wraps to fit the template panel's toolbar. Here it has the line to itself.
  "& .MuiAlert-action .MuiButton-root": {
    maxWidth: "none",
    whiteSpace: "nowrap",
  },
  // On a phone the sentence takes the full width and the action sits under it, with Dismiss at the
  // far end of the same row. The leading icon goes: the button already carries the storefront.
  [theme.breakpoints.down("sm")]: {
    flexWrap: "wrap",
    paddingInline: theme.spacing(1.5),
    "& .MuiAlert-icon": { display: "none" },
    "& .MuiAlert-message": { paddingBottom: 0, width: "100%" },
    "& .MuiAlert-action": {
      justifyContent: "space-between",
      marginInline: 0,
      paddingInline: 0,
      width: "100%",
    },
  },
}));

/**
 * The marketplace team whose published rates a proposal offers on its Fee Schedule tab, or
 * undefined when it offers none.
 *
 * Offered only on the Fee Schedule tab, which is where rates are set, to the vendor (never to the
 * client, #4920), while the vendor may edit the proposal, while its fee schedule has no rates yet,
 * and until the vendor dismisses it. Any proposal qualifies, whatever started it (#6468): the
 * vendor's own draft, or a firm's copy of a proposal sent to several firms that it is revising. An
 * empty fee schedule is what the offer answers. The line keeps its own action: on a phone the draft
 * tab's template panel, which has the same button, starts collapsed.
 */
export function publishedRatesOfferTeamId(params: {
  viewingAsVendor: boolean | undefined;
  /** The proposal's vendor team. Only a team that came from the marketplace publishes rates. */
  vendorTeam: VendorTeam | MaskedTeam | null | undefined;
  /** The rates as they stand: the draft's, or the revision's current entries. */
  feeSchedule: readonly unknown[] | Masked | MaskedField | null | undefined;
  mayEdit: boolean;
  isDismissed: boolean;
  isOnFeeScheduleTab: boolean;
}): Guid | undefined {
  if (!params.mayEdit || params.isDismissed || !params.isOnFeeScheduleTab) {
    return undefined;
  }
  if (Masked.isMasked(params.feeSchedule)) return undefined;
  if (params.feeSchedule && params.feeSchedule.length > 0) return undefined;
  return publishedFeeScheduleTeamId({ viewingAsVendor: params.viewingAsVendor, vendorTeam: params.vendorTeam });
}

type PublishedRatesOfferProps = {
  /** The marketplace team the answer draft's vendor team came from. */
  marketplaceTeamId: Guid;
  /** Called with the schedule the vendor chose. The caller settles staffing and applies it. */
  onApply: (schedule: FeeSchedule) => void;
  onDismiss: () => void;
};

/**
 * Offers a vendor's published rates on a proposal that has none yet (#4920, #6114, #6468).
 *
 * This one line sits at the top of the Fee Schedule tab, on the vendor's draft and on a revision the
 * vendor is writing. On the draft it carries the same **Apply Published Rates** action as the tab's
 * template panel. The vendor presses it, or dismisses the line. Nothing is applied without a press.
 *
 * It draws nothing while the schedules load, or when the team publishes no rates.
 */
export default function PublishedRatesOffer(props: Readonly<PublishedRatesOfferProps>) {
  const { marketplaceTeamId, onApply, onDismiss } = props;
  const { schedules } = usePublishedFeeSchedules(marketplaceTeamId);
  if (schedules.length === 0) return null;

  return (
    <Offer
      severity="info"
      icon={<StorefrontIcon fontSize="inherit" />}
      role="region"
      aria-label="Published rates"
      action={
        <>
          <PublishedFeeScheduleAction marketplaceTeamId={marketplaceTeamId} onApply={onApply} />
          <IconButton aria-label="Dismiss" size="small" onClick={onDismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    >
      {PUBLISHED_RATES_OFFER}
    </Offer>
  );
}
